import React, { useEffect } from 'react';
import { SELECTION_KEY } from '@techchange/pm-specs';
import PropTypes from 'prop-types';
import { EditorView } from 'prosemirror-view';
import ReactDOM from 'react-dom';
import { Popover, PopoverBody } from 'reactstrap';

import { linkSelectionPluginKey } from '../plugins/linkSelection';

import LinkInput from './LinkInput';
import { ignoreInFlightEvent } from '../../../../../../_shared/utils/ignoreInFlightEvent';

const propTypes = {

	/**
	 * Flag specifying if chat view should be rendered.
	 */
	chatView: PropTypes.bool.isRequired,

	/**
	 * Flag specifying if mobile view should be rendered.
	 */
	mobileView: PropTypes.bool.isRequired,

	/**
	 * Set of merge tags that are allowed in links.
	 */
	mergeTags: PropTypes.shape({

		organization: PropTypes.shape({

			domain: PropTypes.bool,

		}),

		invitation: PropTypes.shape({

			id: PropTypes.bool,

		}),

	}),

	/**
	 * The prosemirror editor view object.
	 */
	editorView: PropTypes.instanceOf(EditorView).isRequired,

	/**
	 * The DOM element in which to render the tooltip.
	 */
	editorContainer: PropTypes.instanceOf(Element).isRequired,

};

const LinkTooltip = (props) => {

	const {
		mobileView,
		chatView,
		mergeTags,
		editorView,
	} = props;

	// Get plugin state from current view
	const pluginState = linkSelectionPluginKey.getState(editorView.state);
	const { selectedLink, start, end, showTooltip } = pluginState;

	// Get DOM element of selected text or link
	const linkDOM = selectedLink && editorView.nodeDOM(start);
	const selectionDOM = editorView.nodeDOM(editorView.state.selection.from);
	const target = (linkDOM && linkDOM.parentElement) ||
		(selectionDOM && selectionDOM.parentElement);

	/**
	 * Listen for 'Escape' keydown on window and hide tooltip if pressed.
	 */
	useEffect(() => {

		const hideTooltip = ignoreInFlightEvent((event) => {

			if (event.key === 'Escape') {

				const { tr, schema } = editorView.state;

				if (showTooltip) {
					tr.setMeta('toggleLinkTooltip', true);
					tr.removeMark(0, tr.doc.content.size, schema.mark(SELECTION_KEY, { visible: true }));
				}

				if (selectedLink) {
					tr.setMeta('selectedLink', null);
					tr.setMeta('linkStart', null);
					tr.setMeta('linkEnd', null);
				}

				editorView.dispatch(tr);

				editorView.focus();

			}

		});

		window.addEventListener('keydown', hideTooltip);

		return () => {
			window.removeEventListener('keydown', hideTooltip);
		};

	}, [editorView, selectedLink, showTooltip]);

	if (showTooltip && target) {

		return (
			<Popover
				id="link-input-tooltip"
				className="tc-pm-link-tooltip"
				role="tooltip"
				target={target}
				placement={(chatView || mobileView) ? 'top-start' : 'bottom-start'}
				fade={false}
				hideArrow
				isOpen>
				<PopoverBody>
					<LinkInput
						editorView={editorView}
						href={selectedLink?.attrs?.href}
						start={start}
						end={end}
						mergeTags={mergeTags} />
				</PopoverBody>
			</Popover>
		);

	}

	return <div />;

};

LinkTooltip.propTypes = propTypes;

export default LinkTooltip;
